import { useSyncExternalStore } from 'react';
import { SYNC_TAG } from '../sw-tag.js';
import { captureInstallPrompt, requestPersistentStorage } from './counter-install.js';

/**
 * Service worker registration and update handling for the counter PWA.
 *
 * Two rules shape all of this, and they pull against each other:
 *
 *  1. A new build must actually reach the tills. A counter tablet is opened in
 *     the morning and not closed again, so a worker that waits for every client
 *     to go away waits forever, and a fix to the stamping flow never lands.
 *  2. A reload must never interrupt a transaction. Three seconds from scan to
 *     confirmation (features/STAFF-COUNTER.md) does not survive the page reloading under a
 *     barista's thumb.
 *
 * So: the worker installs in the background and parks, this module notices, and
 * the surfaces decide when to apply it — immediately on the sign-in screen,
 * on a tap or after two idle minutes at the counter. Nothing here decides to
 * reload on its own.
 */

const SW_URL = '/sw.js';

/**
 * Scope, not path — the worker controls `/staff*` only.
 *
 * `sw.js` sits at the root so it *could* claim the whole origin, and the
 * plugin's injected registration did exactly that: an owner who once opened the
 * counter on their phone had their dashboard served from the precached
 * `index.html` afterwards, and a customer opening a pass link installed a worker
 * they have no use for. Narrowing the scope is allowed (a worker may claim less
 * than its directory, never more) and keeps the blast radius at one surface.
 */
const SW_SCOPE = '/staff';

/** Hourly. A till rarely navigates, so nothing else would trigger a check. */
const UPDATE_INTERVAL_MS = 60 * 60_000;

/** Don't re-check on every tab focus — a barista switches apps constantly. */
const FOREGROUND_CHECK_THROTTLE_MS = 15 * 60_000;

/** How long to wait for the new worker to take over before reloading anyway. */
const ACTIVATION_TIMEOUT_MS = 3_000;

/**
 * How many times a session may apply an update *without being asked*.
 *
 * `applyUpdate` reloads whether or not the handover completed, so a build that
 * fails to activate — a broken worker, an activation error, Safari dropping the
 * `skipWaiting` message — leaves the same worker waiting after the reload. An
 * unguarded auto-apply then reloads again, every three seconds, forever, and a
 * till stuck in a reload loop cannot take a payment. Two attempts is enough for
 * a transient failure and short enough that staff are not locked out.
 *
 * The cap covers automatic applications only. The "Update ready" button always
 * works: if an update really is stuck, staff need a way to keep trying, and a
 * tap is a person deciding rather than a timer looping.
 */
const MAX_AUTOMATIC_APPLIES = 2;

const AUTO_APPLY_COUNT_KEY = 'signapass.sw.autoApplies';

let waitingWorker: ServiceWorker | null = null;
let lastCheckedAt = 0;
let reloading = false;

const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot(): boolean {
  return waitingWorker !== null;
}

/**
 * Whether a newer build is installed and waiting to take over.
 *
 * `useSyncExternalStore` rather than context: the source of truth is a browser
 * registration that exists before React mounts, and every surface that cares
 * reads the same one.
 */
export function useUpdateReady(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

function markWaiting(worker: ServiceWorker): void {
  if (waitingWorker === worker) return;
  waitingWorker = worker;
  emitChange();
}

/**
 * How many times this session has auto-applied.
 *
 * `sessionStorage` rather than a module variable: the reload is the whole
 * point, and a module variable resets with it, so the counter would always read
 * zero and the cap would never bite. Guarded like every other storage access —
 * an environment without it simply loses the cap, which is no worse than not
 * having written one.
 */
function readAutoApplyCount(): number {
  try {
    const raw = window.sessionStorage.getItem(AUTO_APPLY_COUNT_KEY);
    const count = Number(raw);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch {
    return 0;
  }
}

function recordAutoApply(count: number): void {
  try {
    window.sessionStorage.setItem(AUTO_APPLY_COUNT_KEY, String(count));
  } catch {
    // No storage, no cap. See readAutoApplyCount.
  }
}

/**
 * Apply a waiting build without a person asking.
 *
 * Used by the sign-in screen and by the counter's idle timer. Declines once the
 * session has already tried `MAX_AUTOMATIC_APPLIES` times, so a build that
 * cannot activate does not put the till in a reload loop.
 */
export function autoApplyUpdate(): void {
  if (!waitingWorker || reloading) return;

  const attempts = readAutoApplyCount();
  if (attempts >= MAX_AUTOMATIC_APPLIES) return;

  // Recorded *before* applying: the reload happens either way, so writing
  // afterwards would never run.
  recordAutoApply(attempts + 1);
  applyUpdate();
}

/**
 * Hand over to the waiting build and reload.
 *
 * The generated worker skips waiting only when asked (`registerType: 'prompt'`
 * in `vite.config.ts`), which is what puts the timing in the caller's hands.
 * Callers are responsible for choosing a moment when nobody is mid-stamp.
 *
 * This is the *deliberate* path — a staff member tapping "Update ready" — and
 * is deliberately uncapped. `autoApplyUpdate` is the one that backs off.
 */
export function applyUpdate(): void {
  const worker = waitingWorker;
  if (!worker || reloading) return;

  const reloadOnce = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', reloadOnce, { once: true });
  worker.postMessage({ type: 'SKIP_WAITING' });

  // If the handover never completes — another tab already activated this
  // worker, or the message is lost — reload anyway. Staying on the old build
  // silently is the failure this whole module exists to prevent.
  window.setTimeout(reloadOnce, ACTIVATION_TIMEOUT_MS);
}

function checkForUpdate(registration: ServiceWorkerRegistration, force = false): void {
  if (!navigator.onLine) return;
  const now = Date.now();
  if (!force && now - lastCheckedAt < FOREGROUND_CHECK_THROTTLE_MS) return;
  lastCheckedAt = now;
  // A failed check is not worth surfacing: the till is offline or the deploy is
  // mid-flight, and the next check is minutes away.
  void registration.update().catch(() => {});
}

function watch(registration: ServiceWorkerRegistration): void {
  // Already installed and parked — a previous session downloaded it.
  if (registration.waiting && registration.active) {
    markWaiting(registration.waiting);
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener('statechange', () => {
      // Without an active worker in *this* registration this is the first
      // install, not an update: there is no older build to replace and nothing
      // to prompt about. Not `navigator.serviceWorker.controller`, which can
      // belong to a different registration than the one being installed.
      if (installing.state === 'installed' && registration.active) {
        markWaiting(installing);
      }
    });
  });

  window.setInterval(() => checkForUpdate(registration, true), UPDATE_INTERVAL_MS);
  window.addEventListener('online', () => checkForUpdate(registration, true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate(registration);
  });
}

/**
 * The iOS tags that make a home-screen icon open as a standalone app.
 *
 * Injected here rather than written into `index.html`, because all three
 * surfaces share that file. A global `apple-mobile-web-app-capable` would mean
 * a customer following the add-to-home-screen tip on `/c` got a standalone app
 * called "Counter", wearing the counter's identity and running in iOS's
 * separate standalone storage — where their login cookie does not exist, so the
 * icon would open a sign-in screen every time and `isStandalone()` would then
 * suppress the tip for good.
 *
 * Safari reads these from the live DOM at the moment somebody taps "Add to
 * Home Screen", so injecting them on navigation is as good as shipping them in
 * the markup — and unlike the markup, it can be scoped to one surface.
 *
 * `black`, not `black-translucent`: translucent combines with the page's
 * `viewport-fit=cover` to run the web view under the status bar, and the
 * counter's header — staff name, update button, sign out — would sit under the
 * clock. An opaque bar is the honest fix; the safe-area padding on the counter
 * shell covers the home indicator and landscape insets.
 */
function applyCounterAppMeta(): void {
  const tags = [
    ['apple-mobile-web-app-capable', 'yes'],
    ['apple-mobile-web-app-status-bar-style', 'black'],
    ['apple-mobile-web-app-title', 'SignaPass'],
  ];

  for (const [name, content] of tags) {
    if (document.querySelector(`meta[name="${name}"]`)) continue;
    const meta = document.createElement('meta');
    meta.name = name as string;
    meta.content = content as string;
    document.head.appendChild(meta);
  }
}

// One definition, shared with the worker — see `src/sw-tag.ts` for why it is
// its own module rather than a literal on either side. Imported *and*
// re-exported: the re-export alone does not bring the name into scope here.
export { SYNC_TAG };

/**
 * Ask the browser to drain the queue when the connection returns, page or no
 * page — features/STAFF-COUNTER.md.
 *
 * ## Why this is worth having on top of the poll
 *
 * `useOfflineQueue` polls every thirty seconds and flushes on reconnect, which
 * covers a shift. What it cannot cover is the case the API exists for: a tablet
 * locked at the end of the day with stamps still queued. The interval dies with
 * the tab, so those stamps wait for somebody to open the app — on a closed
 * Monday, that is Tuesday.
 *
 * ## Feature-detected, and silent when unavailable
 *
 * Background Sync is a Chromium extension, not a cross-browser standard: Safari
 * has no `SyncManager` at all, and a counter on an iPad is a real deployment.
 * features/STAFF-COUNTER.md asks for it "where available, with an interval poll fallback" — so this
 * returns quietly and the poll remains the thing that actually works everywhere.
 *
 * Returns whether a sync was registered, so the caller can say which mechanism
 * is in play rather than assuming.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    // `sync` is absent on Safari and on Firefox. Checked on the registration
    // rather than on `window`, because that is where it lives.
    const sync = (registration as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    }).sync;
    if (!sync) return false;

    await sync.register(SYNC_TAG);
    return true;
  } catch {
    /**
     * Registration throws where the permission is denied — Chrome ties
     * Background Sync to the site's notification-adjacent permission state, and
     * a user who has blocked it gets a rejected promise rather than a missing
     * API. Nothing to do about it and nothing to tell the barista: the poll
     * still runs.
     */
    return false;
  }
}

/**
 * Register the worker and claim the counter's app identity — on `/staff` only.
 *
 * Called unconditionally from `main.tsx`; the surface check lives here so there
 * is exactly one place that decides which surface is a PWA, next to the scope
 * it registers with and the metadata it installs under.
 */
export function registerCounterServiceWorker(): void {
  // `/staff` and below only — not `/staffing` or a mistyped `/staffx`, which are
  // the not-found page and must not take on the counter's identity.
  if (!/^\/staff(\/|$)/.test(window.location.pathname)) return;

  applyCounterAppMeta();
  // Synchronously, before the first render: Chromium fires the install prompt
  // once and early, and a listener added by the pairing screen would miss it.
  captureInstallPrompt();
  void requestPersistentStorage();

  if (!('serviceWorker' in navigator)) return;

  const start = () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: SW_SCOPE })
      .then(watch)
      // A registration failure must not take the app down with it: without a
      // worker the counter is an ordinary web page, which is v1's behaviour
      // anyway once the connection drops.
      .catch(() => {});
  };

  // Registration waits for load so it never competes with the first render for
  // bandwidth — but only if load is still coming. Waiting for an event that has
  // already fired would mean never registering at all.
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
